import { StateEmailParagraphState } from '@urbanx/agx.agencies.client';
import { createAgxForm, createFormDefinition } from '@urbanx/agx.forms';
import { z } from 'zod';

export const paragraphText = (html: string) => {
  const { body } = new DOMParser().parseFromString(html, 'text/html');
  body.querySelectorAll('br').forEach((lineBreak) => lineBreak.replaceWith('\n'));
  body.querySelectorAll('p').forEach((paragraph) => {
    if (paragraph.nextSibling) paragraph.after('\n\n');
  });
  return body.textContent ?? '';
};

export const isBlankParagraph = (html: string) =>
  !paragraphText(html).replaceAll('\u200b', '').trim();

export const editedParagraphSchema = z
  .string()
  .refine((html) => paragraphText(html).length <= 1500, {
    message: 'Use no more than 1,500 characters.',
  });

export const emailParagraphSchema = z.array(
  z.object({
    state: z.enum(StateEmailParagraphState),
    paragraph: z.string(),
  })
);

export const emailTemplateSettingsSchema = z.object({
  agreementToVendor: z.object({ paragraphs: emailParagraphSchema }),
  executedContractToBuyer: z.object({ paragraphs: emailParagraphSchema }),
  executedContractToVendor: z.object({ paragraphs: emailParagraphSchema }),
});

export const EmailTemplatesForm = createAgxForm(
  createFormDefinition({
    id: 'staff-email-templates',
    schema: z.object({ settings: emailTemplateSettingsSchema, eTag: z.string().min(1) }),
    fieldOrder: [
      'settings.agreementToVendor.paragraphs',
      'settings.executedContractToBuyer.paragraphs',
      'settings.executedContractToVendor.paragraphs',
    ],
  })
);

export const EmailParagraphForm = createAgxForm(
  createFormDefinition({
    id: 'staff-email-paragraph',
    schema: z.object({ paragraph: editedParagraphSchema }),
    fieldOrder: ['paragraph'],
  })
);

export const emailTypes = [
  {
    key: 'agreementToVendor',
    title: 'Agreement Email to Vendor',
    description: 'Added to the email sent to vendors with the listing agreement to sign.',
  },
  {
    key: 'executedContractToBuyer',
    title: 'Executed Contract to Buyer',
    description: 'Added to the email sent to the buyer once the contract is executed.',
  },
  {
    key: 'executedContractToVendor',
    title: 'Executed Contract to Vendor',
    description: 'Added to the email sent to the vendor once the contract is executed.',
  },
] as const;

export type EmailType = (typeof emailTypes)[number]['key'];

export const previewTemplateName = (type: EmailType, state: StateEmailParagraphState) => {
  if (type === 'agreementToVendor') return 'ListingAgreementVendorTemplate';
  const recipient = type === 'executedContractToBuyer' ? 'Buyer' : 'Vendor';
  return `${state === StateEmailParagraphState.NSW ? 'NswExchangedContract' : 'ExecutedContract'}${recipient}Template`;
};

export const renderEmailPreview = (content: string, html: string) =>
  content
    .replace(/\{\{\{?([^}]+)\}\}\}?/g, (placeholder, expression: string) => {
      const name = expression.trim();
      if (name.toLowerCase() === 'customparagraph') return paragraphText(html);
      // Campaign data is unavailable, so keep the conditions around each possible branch.
      if (/^[#/^]|^else(?:\s|$)/.test(name)) return placeholder;
      return `[${expression}]`;
    })
    .trim();
